import { Pagination } from 'swiper/modules';
import { Swiper, SwiperSlide } from 'swiper/react';

import ImageProxy from '@next-image-proxy';

import { List } from '../../ui/List';
import type { ProjectContentTableOfContentTab } from '../types';
import {
  businessModel,
  gallery,
  introductionBenefits,
  keyFeatures,
  marketTraction,
  problemsSolves,
} from './constants';

export const tableOfContentTab: ProjectContentTableOfContentTab['content'] = [
  {
    id: 'intro',
    title: 'Introduction',
    content: (
      <>
        <p className="max-sm:text-sm font-normal">
          Primex Finance is a highly flexible leveraged trading and yield farming protocol
          addressing overcollateralization, limited asset availability on exchanges, and the lack of
          automation in DeFi.
        </p>
        <p className="max-sm:text-sm font-normal">
          On Primex, borrowers utilize lender liquidity to leverage low-risk assets like LSTs and
          LRTs, RWAs, DEX LP positions as well as margin trade assets on spot DEXs, including those
          not available on CEXs including memecoins and AI agents.
        </p>
        <p className="max-sm:text-sm font-normal">
          Fully open and accessible, Primex requires no minimum amounts or de-anonymization, making
          it open for everyone from institutional funds to retail users with smaller capital.
        </p>
        <p className="max-sm:text-sm font-normal mb-4">With Primex, users can benefit from:</p>
        <List items={introductionBenefits} />
        <p className="mt-4 max-sm:text-sm font-normal">
          In its next version, the protocol will offer an advanced cross-margin portfolio with
          multiple positions in liquid staking, restaking, RWAs, and DEX LP tokens, all managed
          under a unified health factor and AI-powered rebalancing for yield optimization.
        </p>
        <p className="max-sm:text-sm font-normal">
          Primex Finance integrates these features into a single, user-friendly platform with
          intuitive interfaces. It’s designed to serve the needs of traders, lenders, and yield
          farmers, offering advanced tools for seasoned DeFi users while remaining accessible to
          newcomers.
        </p>
        <p className="mb-6 max-sm:text-sm font-normal">
          Explore advanced strategies, grow your portfolio, and unlock the full potential of
          decentralized finance with Primex Finance.
        </p>
        <div className="overflow-hidden">
          <Swiper
            modules={[Pagination]}
            pagination={{
              enabled: true,
              clickable: true,
            }}
            slidesPerView="auto"
            className="pb-8"
          >
            {gallery.map((galleryItem, index) => (
              <SwiperSlide key={index} className="w-4/5 px-1">
                <ImageProxy
                  alt={`primex-finance-${index}`}
                  width={2560}
                  height={1300}
                  quality={100}
                  className="w-full h-auto select-none"
                  src={galleryItem.image}
                />
              </SwiperSlide>
            ))}
          </Swiper>
        </div>
      </>
    ),
  },
  {
    id: 'key-features',
    title: 'Key Features',
    content: (
      <>
        <p className="mb-4 max-sm:text-sm font-semibold">
          Primex operates as a comprehensive ecosystem, offering:
        </p>
        <List items={keyFeatures} />
      </>
    ),
  },
  {
    id: 'problems-solves',
    title: 'Problems Primex solves',
    content: <List items={problemsSolves} />,
  },
  {
    id: 'traction-achievements',
    title: 'Traction / Achievements',
    content: <List items={marketTraction} />,
  },
  {
    id: 'investors',
    title: 'Investors',
    content: (
      <ImageProxy
        alt="Investors"
        src="/launchpad/stories/primex-finance/investors.png"
        className="block w-full h-auto mt-10"
        quality={100}
        width={773}
        height={376}
      />
    ),
  },
  {
    id: 'partners',
    title: 'Partners',
    content: (
      <ImageProxy
        alt="Partners"
        src="/launchpad/stories/primex-finance/partners.png"
        className="block w-full h-auto mt-10"
        quality={100}
        width={773}
        height={364}
      />
    ),
  },
  {
    id: 'business-model',
    title: 'Business Model',
    content: (
      <>
        <List items={businessModel} className="my-10" />
        <ImageProxy
          alt="Business Model"
          src="/launchpad/stories/primex-finance/business-model.png"
          className="block w-full h-auto"
          quality={100}
          width={773}
          height={183}
        />
      </>
    ),
  },
  {
    id: 'token-utility',
    title: 'Tokenomics and Token Utility',
    content: (
      <>
        <ImageProxy
          alt="Tokenomics and Token Utility"
          src="/launchpad/stories/primex-finance/tokenomics.png"
          className="block w-full h-auto mt-10"
          quality={100}
          width={773}
          height={288}
        />
        <ImageProxy
          alt="Token Distribution Part 1"
          src="/launchpad/stories/primex-finance/token-distribution.png"
          className="block w-full h-auto mt-10"
          quality={100}
          width={773}
          height={387}
        />
        <ImageProxy
          alt="Token Distribution Part 2"
          src="/launchpad/stories/primex-finance/token-distribution-2.png"
          className="block w-full h-auto mt-10"
          quality={100}
          width={773}
          height={522}
        />
      </>
    ),
  },
  {
    id: 'team',
    title: 'Team',
    content: (
      <ImageProxy
        alt="Team"
        src="/launchpad/stories/primex-finance/team.png"
        className="block w-full h-auto mt-10"
        quality={100}
        width={773}
        height={334}
      />
    ),
  },
  {
    id: 'roadmap',
    title: 'Roadmap',
    content: (
      <ImageProxy
        alt="Roadmap"
        src="/launchpad/stories/primex-finance/roadmap.png"
        className="block w-full h-auto mt-10"
        quality={100}
        width={773}
        height={331}
      />
    ),
  },
];
